import { LogoutSuccessAction, LOGOUT_SUCCESS } from '../actions/login';
import { keyBy, uniq, groupBy, forIn, concat, forEach, reduce, unset } from 'lodash';
import { DashboardState, Organization, ReleaseEntity, App as AppModel } from '../types';
import { combineReducers } from 'redux';
import {
    ConnectedReleaseResponse,
    DeleteReleaseEntityAction,
    DeleteReleaseEntityPayload,
    DeleteReleasesAction,
    DELETE_RELEASES,
    DELETE_RELEASE_ENTITY,
    FetchReleasesSuccessAction,
    FETCH_RELEASES_SUCCESS,
    NewReleaseSuccessAction,
    NEW_RELEASE_SUCCESS,
    ReleaseStateUpdatedAction,
    ReleasesUpdatedAction,
    RELEASES_UPDATED,
    RELEASE_STATE_UPDATED,
    UPDATE_RELEASE_ENTITY_SUCCESS,
    UpdateReleaseEntitySuccessAction,
} from '../actions/release';
import {
    AppsAddedSuccessAction,
    AppsDeletedAction,
    AppStateUpdatedAction,
    AppsUpdatedAction,
    APPS_ADDED_SUCCESS,
    APPS_DELETED,
    APPS_UPDATED,
    APP_STATE_UPDATED,
    ConnectedAppEntityResponse,
    DeletedAppEntityPayload,
} from '../actions/app';
import {
    AppGroupsDeletedAction,
    AppGroupStateUpdatedAction,
    AppGroupsUpdatedAction,
    APP_GROUPS_DELETED,
    APP_GROUPS_UPDATED,
    APP_GROUP_STATE_UPDATED,
    ConnectedAppGroupEntityResponse,
    DeletedAppGroupPayload,
} from '../actions/appGroups';
import { ClearStateAction, CLEAR_STATE, FetchStateSuccessAction, FETCH_STATE_SUCCESS } from '../actions/state';
import { FETCH_APP_STATE_SUCCESS, FetchAppStateSuccessAction } from '../actions/app';

interface Org {
    id: string;
    appIds: string[];
}

interface App {
    id: string;
    releaseIds: string[];
}

interface AppAppGroup {
    id: string; // the app id
    appGroupId: string;
}

interface OrgAppGroups {
    id: string; // org id
    appGroupIds: string[];
}

interface AppsInOrg {
    [id: string]: Org;
}

interface ReleasesInApp {
    [id: string]: App;
}

interface AppGroupsInOrg {
    [id: string]: OrgAppGroups;
}

interface AppGroupsForApp {
    [id: string]: AppAppGroup;
}

type AppsInOrgActions =
    | FetchStateSuccessAction
    | AppStateUpdatedAction
    | LogoutSuccessAction
    | AppsAddedSuccessAction
    | AppsUpdatedAction
    | AppsDeletedAction
    | ClearStateAction;

type ReleasesInAppActions =
    | FetchStateSuccessAction
    | FetchAppStateSuccessAction
    | NewReleaseSuccessAction
    | ReleaseStateUpdatedAction
    | ReleasesUpdatedAction
    | DeleteReleaseEntityAction
    | DeleteReleasesAction
    | LogoutSuccessAction
    | AppsAddedSuccessAction
    | AppsDeletedAction
    | ClearStateAction
    | UpdateReleaseEntitySuccessAction
    | FetchReleasesSuccessAction;

type AppGroupsInOrgActions =
    | FetchStateSuccessAction
    | LogoutSuccessAction
    | AppGroupStateUpdatedAction
    | AppGroupsUpdatedAction
    | AppGroupsDeletedAction
    | ClearStateAction;

type AppGroupsForAppActions =
    | FetchStateSuccessAction
    | LogoutSuccessAction
    | AppsDeletedAction
    | AppsAddedSuccessAction
    | AppsUpdatedAction
    | ClearStateAction;

function generateAppsInOrg(organizations: Organization[]): AppsInOrg {
    const orgs = organizations.map((org) => {
        return { id: org.id, appIds: org.apps.map((app) => app.id) };
    });

    return keyBy(orgs, (org) => org.id);
}

function updateAppsInOrg(state: AppsInOrg = {}, payload: ConnectedAppEntityResponse) {
    const appIds = state[payload.orgId].appIds.concat([payload.app.id]);

    return {
        ...state,
        [payload.orgId]: {
            id: payload.orgId,
            appIds: uniq(appIds),
        },
    };
}

function updateApps(state: AppsInOrg, payload: ConnectedAppEntityResponse[]) {
    let updatedState = state;
    const groupedAppsByOrgId = groupBy(payload, (connectedApp) => connectedApp.orgId);

    forIn(groupedAppsByOrgId, (connectedApps, orgId) => {
        const currentAppIds = state[orgId]?.appIds ?? [];
        const appIds = concat(
            currentAppIds,
            connectedApps.map((connectedApp) => connectedApp.app.id),
        );

        updatedState = {
            ...updatedState,
            [orgId]: {
                id: orgId,
                appIds: uniq(appIds),
            },
        };
    });

    return updatedState;
}

function deleteAppInOrg(state: AppsInOrg, payload: DeletedAppEntityPayload) {
    const org = state[payload.orgId];

    if (org) {
        const updatedAppIds = org.appIds.filter((appId) => appId !== payload.appId);

        return {
            ...state,
            [payload.orgId]: {
                id: payload.orgId,
                appIds: [...updatedAppIds],
            },
        };
    }

    return state;
}

function deleteAppsInOrg(state: AppsInOrg, payload: DeletedAppEntityPayload[]) {
    return reduce(
        payload,
        (currState, deleteAppEntity) => {
            return deleteAppInOrg(currState, deleteAppEntity);
        },
        state,
    );
}

// A dictionary of orgIds mapping to objects containing the id of the org and an array of appIds for that org
/*
    appsInOrg: {
        1: {
            id: 1,
            appIds: [2, 3, 4]
        }
    }
 */
const appsInOrg = (state: AppsInOrg = {}, action: AppsInOrgActions) => {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            if (action.payload.organizations) {
                return generateAppsInOrg(action.payload.organizations);
            }

            return state;
        case APP_STATE_UPDATED:
            return updateAppsInOrg(state, action.payload);
        case APPS_UPDATED:
        case APPS_ADDED_SUCCESS:
            return updateApps(state, action.payload);
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return {};
        case APPS_DELETED:
            return deleteAppsInOrg(state, action.payload);
        default:
            return state;
    }
};

function generateReleasesInApp(state: ReleasesInApp = {}, app: AppModel): ReleasesInApp {
    const releaseIds = app.releases.map((release) => release.id);

    return {
        ...state,
        [app.id]: {
            id: app.id,
            releaseIds: uniq(releaseIds),
        },
    };
}

function updateReleasesInApp(state: ReleasesInApp = {}, appId: string, releases: ReleaseEntity[]) {
    const releasesIds = releases.map((release) => release.id);
    let updatedReleaseIds = [];

    if (state[appId]) {
        updatedReleaseIds = state[appId].releaseIds.concat(releasesIds);
    } else {
        updatedReleaseIds = releasesIds;
    }

    return {
        ...state,
        [appId]: {
            id: appId,
            releaseIds: uniq(updatedReleaseIds),
        },
    };
}

function updateReleases(state: ReleasesInApp, payload: ConnectedReleaseResponse[]) {
    let updatedState = state;
    const groupedReleasesByAppId = groupBy(payload, (connectedRelease) => connectedRelease.appId);

    forIn(groupedReleasesByAppId, (releases, appId) => {
        const currentReleaseIds = state[appId]?.releaseIds ?? [];
        const releaseIds = concat(
            currentReleaseIds,
            releases.map((connectedRelease) => connectedRelease.release.id),
        );

        updatedState = {
            ...updatedState,
            [appId]: {
                id: appId,
                releaseIds: uniq(releaseIds),
            },
        };
    });

    return updatedState;
}

function deleteReleaseInApp(state: ReleasesInApp, payload: DeleteReleaseEntityPayload) {
    const app = state[payload.appId];

    if (app) {
        const updatedReleaseIds = app.releaseIds.filter((releaseId) => releaseId !== payload.releaseId);

        return {
            ...state,
            [payload.appId]: {
                id: payload.appId,
                releaseIds: [...updatedReleaseIds],
            },
        };
    }

    return state;
}

function deleteReleasesInApp(state: ReleasesInApp, payload: DeleteReleaseEntityPayload[]) {
    return reduce(
        payload,
        (currState, deleteReleaseEntity) => {
            return deleteReleaseInApp(currState, deleteReleaseEntity);
        },
        state,
    );
}

function deleteApps(state: ReleasesInApp, payload: DeletedAppEntityPayload[]) {
    const updatedState: ReleasesInApp = {
        ...state,
    };

    forEach(payload, (deletedAppEntity) => {
        unset(updatedState, deletedAppEntity.appId);
    });

    return updatedState;
}

function addAppsToReleasesInApp(state: ReleasesInApp, payload: ConnectedAppEntityResponse[]): ReleasesInApp {
    const updatedState = {
        ...state,
    };

    forEach(payload, (connectedApp) => {
        updatedState[connectedApp.app.id] = {
            id: connectedApp.app.id,
            releaseIds: [],
        };
    });

    return updatedState;
}

// A dictionary of appIds mapping to objects containing the id of the app and an array of releaseIds for that app
/*
    releasesInApp: {
        1: {
            id: 1,
            releaseIds: [2, 3, 4]
        }
    }
 */
const releasesInApp = (state: ReleasesInApp = {}, action: ReleasesInAppActions) => {
    switch (action.type) {
        case FETCH_APP_STATE_SUCCESS:
            return generateReleasesInApp(state, action.payload.app);
        case APPS_ADDED_SUCCESS:
            return addAppsToReleasesInApp(state, action.payload);
        case NEW_RELEASE_SUCCESS:
        case RELEASE_STATE_UPDATED:
        case UPDATE_RELEASE_ENTITY_SUCCESS:
            return updateReleasesInApp(state, action.payload.appId, [action.payload.release]);
        case FETCH_RELEASES_SUCCESS:
            return updateReleasesInApp(state, action.payload.appId, action.payload.releases);
        case RELEASES_UPDATED:
            return updateReleases(state, action.payload);
        case DELETE_RELEASES:
            return deleteReleasesInApp(state, action.payload);
        case DELETE_RELEASE_ENTITY:
            return deleteReleaseInApp(state, action.payload);
        case APPS_DELETED:
            return deleteApps(state, action.payload);
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
};

// A dictionary of orgIds mapping to objects containing the id of the org and an array of appGroupIds for that org
/*
    appGroupsInOrg: {
        1: {
            id: 1,
            appGroupIds: ["1", "2", "3"]
        }
    }

*/

const generateAppGroupsInOrg = (state: AppGroupsInOrg, payload: DashboardState) => {
    const appGroupsInOrg = {};

    forEach(payload.organizations, (org) => {
        const appGroupIds = org.appGroups.map((appGroup) => appGroup.id);

        appGroupsInOrg[org.id] = {
            id: org.id,
            appGroupIds,
        };
    });

    return {
        ...state,
        ...appGroupsInOrg,
    };
};

function updateAppGroupsInOrg(state: AppGroupsInOrg, payload: ConnectedAppGroupEntityResponse) {
    const appGroupIds = state[payload.orgId].appGroupIds.concat([payload.appGroup.id]);

    return {
        ...state,
        [payload.orgId]: {
            id: payload.orgId,
            appGroupIds: uniq(appGroupIds),
        },
    };
}

function updateAppGroups(state: AppGroupsInOrg, payload: ConnectedAppGroupEntityResponse[]) {
    let updatedState = state;
    const groupedAppGroupsByOrgId = groupBy(payload, (connectedAppGroup) => connectedAppGroup.orgId);

    forIn(groupedAppGroupsByOrgId, (connectedAppGroups, orgId) => {
        const currentAppGroupIds = state[orgId]?.appGroupIds ?? [];
        const appGroupIds = concat(
            currentAppGroupIds,
            connectedAppGroups.map((connectedAppGroup) => connectedAppGroup.appGroup.id),
        );

        updatedState = {
            ...updatedState,
            [orgId]: {
                id: orgId,
                appGroupIds: uniq(appGroupIds),
            },
        };
    });

    return updatedState;
}

function deleteAppGroupInOrg(state: AppGroupsInOrg, payload: DeletedAppGroupPayload): AppGroupsInOrg {
    const org = state[payload.orgId];

    if (org) {
        const updatedAppGroupIds = org.appGroupIds.filter((appGroupId) => appGroupId !== payload.appGroupId);

        return {
            ...state,
            [payload.orgId]: {
                id: payload.orgId,
                appGroupIds: [...updatedAppGroupIds],
            },
        };
    }

    return state;
}

function deleteAppGroupsInOrg(state: AppGroupsInOrg, payload: DeletedAppGroupPayload[]): AppGroupsInOrg {
    return reduce(
        payload,
        (currState, deletedAppGroup) => {
            return deleteAppGroupInOrg(currState, deletedAppGroup);
        },
        state,
    );
}

const appGroupsInOrg = (state: AppGroupsInOrg = {}, action: AppGroupsInOrgActions) => {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            return generateAppGroupsInOrg(state, action.payload);
        case APP_GROUP_STATE_UPDATED:
            return updateAppGroupsInOrg(state, action.payload);
        case APP_GROUPS_UPDATED:
            return updateAppGroups(state, action.payload);
        case APP_GROUPS_DELETED:
            return deleteAppGroupsInOrg(state, action.payload);
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
};

// App groups for app - a dictionary of appIds mapping to an array of appGroupIds for the groups they belong to
/*
    appGroupsForApp: {
        1: { // appId
            id: 1,
            appGroupIds: ["1"]
        }
    }

*/

function generateAppGroupForApp(state: AppGroupsForApp, organizations: Organization[]) {
    const appGroupsForApp: AppGroupsForApp = {
        ...state,
    };

    forEach(organizations, (org) => {
        forEach(org.appGroups, (appGroup) => {
            forEach(appGroup.appIds, (appId) => {
                appGroupsForApp[appId] = {
                    id: appId,
                    appGroupId: appGroup.id,
                };
            });
        });
    });

    return appGroupsForApp;
}

const updateAppGroupForApp = (state: AppGroupsForApp, connectedAppEntity: ConnectedAppEntityResponse) => {
    const prevAppGroupsForApp = state[connectedAppEntity.app.id];
    return {
        ...state,
        [connectedAppEntity.app.id]: {
            ...prevAppGroupsForApp,
            id: connectedAppEntity.app.id,
            appGroupId: connectedAppEntity.appGroupId,
        },
    };
};

const updateAppGroupForApps = (state: AppGroupsForApp, connectedAppEntities: ConnectedAppEntityResponse[]) => {
    return reduce(
        connectedAppEntities,
        (currState, connectedAppEntity) => {
            return updateAppGroupForApp(currState, connectedAppEntity);
        },
        state,
    );
};

const deleteAppGroupForApp = (state: AppGroupsForApp, deletedAppEntity: DeletedAppEntityPayload) => {
    const updatedState = {
        ...state,
    };

    unset(updatedState, deletedAppEntity.appId);

    return updatedState;
};

const deleteAppGroupsForApps = (state: AppGroupsForApp, deletedAppEntities: DeletedAppEntityPayload[]) => {
    return reduce(
        deletedAppEntities,
        (currState, deletedAppEntity) => {
            return deleteAppGroupForApp(currState, deletedAppEntity);
        },
        state,
    );
};

const appGroupForApp = (state: AppGroupsForApp = {}, action: AppGroupsForAppActions) => {
    switch (action.type) {
        case FETCH_STATE_SUCCESS:
            return generateAppGroupForApp(state, action.payload.organizations);
        case APPS_DELETED:
            return deleteAppGroupsForApps(state, action.payload);
        case APPS_UPDATED:
        case APPS_ADDED_SUCCESS:
            return updateAppGroupForApps(state, action.payload);
        case CLEAR_STATE:
        case LOGOUT_SUCCESS:
            return {};
        default:
            return state;
    }
};

const relationshipsReducer = combineReducers({
    appsInOrg,
    releasesInApp,
    appGroupsInOrg,
    appGroupForApp,
});

export default relationshipsReducer;
